import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';

/**
 * UFO: Mystery ship that flies across the top of the screen
 * at random intervals, awarding bonus points (50-300) when hit.
 * Features neon red glow and rainbow trail effect.
 */
export class UFO {
  private scene: Phaser.Scene;
  private sprite: Phaser.Physics.Arcade.Sprite | null = null;
  private glowSprite: Phaser.GameObjects.Image | null = null;
  private active = false;
  private trailTimer: Phaser.Time.TimerEvent | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  spawn(fromRight: boolean = false): void {
    if (this.active) return;

    const { width } = this.scene.cameras.main;
    const startX = fromRight ? width + 20 : -20;
    const velocity = fromRight ? -GAME_CONFIG.ufo.speed : GAME_CONFIG.ufo.speed;

    // Create glow sprite behind UFO (neon red glow)
    this.glowSprite = this.scene.add.image(startX, 50, 'ufo');
    this.glowSprite.setScale(1.6);
    this.glowSprite.setAlpha(0.35);
    this.glowSprite.setTint(0xff3333);
    this.glowSprite.setBlendMode(Phaser.BlendModes.ADD);
    this.glowSprite.setDepth(9);

    // Pulsing glow animation
    this.scene.tweens.add({
      targets: this.glowSprite,
      alpha: { from: 0.25, to: 0.5 },
      scale: { from: 1.5, to: 1.8 },
      duration: 300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Create main UFO sprite
    this.sprite = this.scene.physics.add.sprite(startX, 50, 'ufo');
    this.sprite.setVelocityX(velocity);
    this.sprite.setDepth(10);

    this.active = true;

    // Start trail effect - leave glowing particles behind
    this.trailTimer = this.scene.time.addEvent({
      delay: 60,
      callback: () => this.createTrailParticle(),
      loop: true,
    });
  }

  private createTrailParticle(): void {
    if (!this.sprite || !this.active) return;

    // Rainbow glow trail - cycle through colors
    const colors = [0xff3333, 0xff6600, 0xffff00, 0x00ff41, 0x00ffff, 0xff00ff];
    const color = Phaser.Utils.Array.GetRandom(colors);

    const trail = this.scene.add.circle(
      this.sprite.x,
      this.sprite.y,
      Phaser.Math.Between(3, 6),
      color
    );
    trail.setAlpha(0.6);
    trail.setBlendMode(Phaser.BlendModes.ADD);
    trail.setDepth(8);

    // Fade and shrink trail particle
    this.scene.tweens.add({
      targets: trail,
      alpha: 0,
      scale: 0.2,
      duration: Phaser.Math.Between(400, 700),
      onComplete: () => trail.destroy(),
    });
  }

  update(): void {
    if (!this.active || !this.sprite) return;

    // Sync glow sprite position
    if (this.glowSprite) {
      this.glowSprite.x = this.sprite.x;
      this.glowSprite.y = this.sprite.y;
    }

    const { width } = this.scene.cameras.main;
    // Destroy if off-screen
    if (this.sprite.x < -40 || this.sprite.x > width + 40) {
      this.destroy();
    }
  }

  getSprite(): Phaser.Physics.Arcade.Sprite | null {
    return this.sprite;
  }

  isActive(): boolean {
    return this.active;
  }

  getBonusPoints(): number {
    return Phaser.Math.Between(
      GAME_CONFIG.scoring.ufoMinPoints,
      GAME_CONFIG.scoring.ufoMaxPoints
    );
  }

  destroy(): void {
    // Clean up trail timer
    if (this.trailTimer) {
      this.trailTimer.destroy();
      this.trailTimer = null;
    }
    // Clean up glow sprite
    if (this.glowSprite) {
      this.scene.tweens.killTweensOf(this.glowSprite);
      this.glowSprite.destroy();
      this.glowSprite = null;
    }
    // Clean up main sprite
    if (this.sprite) {
      this.sprite.destroy();
      this.sprite = null;
    }
    this.active = false;
  }
}
